/**
 * Three-way theme control (System / Light / Dark) for the admin topbar.
 *
 * Reads + writes via ``useTheme`` (ThemeProvider). Rendered inside
 * ``AdminLayout`` only — the operator surface is the sole place dark
 * mode applies. Styled entirely with design tokens so it themes with
 * everything else.
 */

import { useTranslation } from "react-i18next";

import { useTheme } from "@/components/ThemeProvider";
import { cx } from "@/lib/utils";
import type { ThemeChoice } from "@/lib/theme";

function MonitorIcon(): JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

function SunIcon(): JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

function MoonIcon(): JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
    </svg>
  );
}

const OPTIONS: { value: ThemeChoice; Icon: () => JSX.Element; i18nKey: string }[] = [
  { value: "system", Icon: MonitorIcon, i18nKey: "theme.system" },
  { value: "light", Icon: SunIcon, i18nKey: "theme.light" },
  { value: "dark", Icon: MoonIcon, i18nKey: "theme.dark" },
];

export function ThemeToggle(): JSX.Element | null {
  const { t } = useTranslation("admin");
  const { choice, setChoice, themeEnabled } = useTheme();

  // Marketing shell doesn't offer dark mode — nothing to toggle.
  if (!themeEnabled) return null;

  return (
    <div
      role="radiogroup"
      aria-label={t("theme.label")}
      className="inline-flex items-center rounded-md border border-border-strong bg-surface p-0.5"
      data-testid="theme-toggle"
    >
      {OPTIONS.map(({ value, Icon, i18nKey }) => {
        const active = choice === value;
        const label = t(i18nKey);
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={label}
            title={label}
            onClick={() => setChoice(value)}
            className={cx(
              "inline-flex h-7 w-7 items-center justify-center rounded transition-colors",
              active
                ? "bg-surface-3 text-ink"
                : "text-ink-4 hover:bg-surface-2 hover:text-ink-2",
            )}
            data-testid={`theme-toggle-${value}`}
          >
            <Icon />
          </button>
        );
      })}
    </div>
  );
}
